import { RefObject, useEffect, useRef, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { IconInfoCircle, IconX } from '@tabler/icons-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Checkbox,
  Modal,
  ModalBody,
  TextFieldType,
  Typography,
} from '@cfa/react-components';
import { Focusable, MessageBlock, Tooltip } from '@cfa/react-core';

import useAuth from '@/hooks/useAuth';
import { usePlanLocations } from '@/hooks/usePlanLocations';

import { itemByLanguage, pluralize } from '@/utils/language';
import { cn } from '@/utils/tailwind';

import LocationsFilter from '@/components/LocationsFilter/LocationsFilter';
import ScrollModalFooter from '@/components/ScrollModal/ScrollModalFooter';
import ScrollModalHeader from '@/components/ScrollModal/ScrollModalHeader';
import Searchbar from '@/components/Searchbar/Searchbar';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import Constants from '@/constants';

import { AssignedTeamMembersCount } from '../AssignedTeamMembersCount';
import TeamMemberCard from '../TeamMemberCard';
import { usePatchAutoAssign } from '../usePatchAutoAssign';
import { useTeamMemberData } from '../useTeamMemberData';

import { AutoAssignToggle } from './components';
import { useModalDurationTracking } from './utils';

import { PathwayUser, Plan } from '@/_generated/api-clients/xp';
import { useAmplitudeTrack } from '@/amplitude/useAmplitude';
import { AlreadyAssignedXsm, CompletedXsm } from '@/icons';

interface AddTeamMembersPopUpProps {
  isOpen: boolean;
  onClose: () => void;
  plan?: Plan;
  isCompliance?: boolean;
  isTrainingMode?: boolean;
  handleNextClick: (selection: {
    isTrainingMode: boolean;
    selectedTeamMembers: PathwayUser[];
    id?: string;
    /** Auto assign option selected. */
    autoAssign: boolean;
  }) => void;
  reassignEligible?: boolean;
  teamMemberQueryOptions?: Record<string, unknown>;
  /** Whether to show the auto assign toggle. */
  showAutoAssignToggle?: boolean;
}

const AddTeamMembersPopUp: React.FC<AddTeamMembersPopUpProps> = ({
  isOpen,
  onClose,
  plan,
  isCompliance,
  isTrainingMode,
  reassignEligible = false,
  handleNextClick,
  teamMemberQueryOptions,
  showAutoAssignToggle = false,
}) => {
  const { t } = useTranslation();
  const { autoAssignPlansEpic: autoAssignPlansEpicFF } = useFlags();
  const track = useAmplitudeTrack();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const modalBodyRef = useRef<HTMLDivElement>(
    null,
  ) as RefObject<HTMLDivElement>;

  const patchAutoAssign = usePatchAutoAssign();

  const amplitudeEventProperties = {
    plan_id: plan?.id,
    plan_name: itemByLanguage(plan?.name),
    is_compliance: isCompliance ?? false,
  };

  const planAutoAssign = !!plan?.autoAssign;
  const [autoAssignToggle, setAutoAssignToggle] = useState(planAutoAssign);

  useEffect(() => {
    setAutoAssignToggle(isCompliance ? true : planAutoAssign);
  }, [planAutoAssign, isCompliance]);

  const autoAssignToggleDifferentFromPlan = autoAssignToggle !== planAutoAssign;

  useModalDurationTracking(isOpen, amplitudeEventProperties);

  const [searchTermDisplayed, setSearchTermDisplayed] = useState('');
  const [teamMembersPerPage, setTeamMembersPerPage] = useState(12);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<PathwayUser[]>(
    [],
  );
  const [filterBy, setFilterBy] = useState<string[]>();
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [showBanner, setShowBanner] = useState(
    () => localStorage.getItem('reassignment-banner-dismissed') !== 'true',
  );
  const [hidePreviouslyAssigned, setHidePreviouslyAssigned] = useState(
    isCompliance ? true : false,
  );
  const [isInfoTooltipOpen, setIsInfoTooltipOpen] = useState(false);

  const { data: auth } = useAuth();
  const getLocationsByPlan = usePlanLocations();

  // LearnUpon compliance plans don't have a Plan object (isPlanQueryEnabled is
  // false), so we fall back to the user's trainer+ locations.
  const locations = plan
    ? getLocationsByPlan(plan)
    : isCompliance
      ? (auth?.assignableTrainerLocations ?? [])
      : [];

  const {
    assignments,
    currentCount,
    filteredTeamMembers,
    selectableTeamMembers,
    alreadyAssignedTeamMembers,
    getTeamMemberPlanStatus,
    getCompletionDate,
    reassignPlansEpic,
  } = useTeamMemberData({
    isOpen,
    locations,
    plan,
    isCompliance,
    isTrainingMode,
    searchTerm: searchTermDisplayed,
    filterBy,
    hidePreviouslyAssigned,
    selectedTeamMembers,
    teamMemberQueryOptions,
  });

  const showReassignmentFeatures =
    reassignPlansEpic && !isTrainingMode && reassignEligible;

  // Track if modal was open so we only dismiss banner on actual close (not initial mount)
  const wasOpenRef = useRef(false);

  // Mark banner as dismissed when modal closes (user leaves)
  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
    } else if (wasOpenRef.current && showBanner && showReassignmentFeatures) {
      localStorage.setItem('reassignment-banner-dismissed', 'true');
      setShowBanner(false);
      wasOpenRef.current = false;
    }
  }, [isOpen, showBanner, showReassignmentFeatures]);

  const onClearSearchClick = () => {
    setSearchTermDisplayed('');
    searchInputRef.current?.focus();
  };

  const onSearchInputChange = (e?: React.ChangeEvent<TextFieldType>) => {
    setIsSelectAll(false);
    setSearchTermDisplayed(e?.target.value ?? '');
  };

  const onLoadMore = () => {
    setTeamMembersPerPage(teamMembersPerPage + 12);
  };

  const onAssignTeamMember = (member: PathwayUser) => {
    setIsSelectAll(false);
    const isSelected = selectedTeamMembers.find(
      selectedTeamMember => selectedTeamMember.adId === member.adId,
    );
    if (isSelected) {
      setSelectedTeamMembers(
        selectedTeamMembers.filter(
          selectedTeamMember => selectedTeamMember.adId !== member.adId,
        ),
      );
    } else {
      setSelectedTeamMembers([...selectedTeamMembers, member]);
    }
  };

  const onAssignAllTeamMembers = () => {
    const combinedSelectedTeamMembers = [
      ...new Set([...selectedTeamMembers, ...(selectableTeamMembers ?? [])]),
    ];
    setIsSelectAll(prev => !prev);
    setSelectedTeamMembers(!isSelectAll ? combinedSelectedTeamMembers : []);
  };

  const onChipClick = (location: string) => {
    if (filterBy?.includes(location)) {
      setFilterBy(filterBy.filter(it => it !== location));
    } else {
      setFilterBy(filter => [...(filter ?? []), location]);
    }
  };

  const resetCount = () => {
    setSelectedTeamMembers([]);
  };

  const onNextClick = async () => {
    if (autoAssignToggle && autoAssignToggleDifferentFromPlan) {
      track?.('auto_assign_enabled', amplitudeEventProperties);
    }

    if (selectedTeamMembers.length === 0 && autoAssignToggleDifferentFromPlan) {
      const planId = plan?.id;
      if (!planId) {
        datadogRum.addError(
          new Error('[AddTeamMembersPopUp] No plan ID found'),
        );
        return;
      }
      try {
        await patchAutoAssign.mutateAsync({
          path: { planId },
          body: {
            autoAssign: autoAssignToggle,
            ...(autoAssignToggle ? { dueDateOffsetInDays: 14 } : {}),
          },
        });
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id} severity="success">
            {t('autoAssignToggle.assignmentUpdated')}
          </ToastMessageBlock>
        ));
      } catch (err) {
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id} severity="error">
            {err instanceof Error ? err.message : String(err)}
          </ToastMessageBlock>
        ));
      } finally {
        onClose();
      }
      return;
    }

    if (selectedTeamMembers.length > 0) {
      track?.('team_members_assigned', {
        ...amplitudeEventProperties,
        num_team_members: selectedTeamMembers.length,
        total_selectable_team_members: selectableTeamMembers?.length ?? 0,
        is_training_mode: !!isTrainingMode,
        auto_assign_also_enabled: autoAssignToggle,
      });
    }

    handleNextClick({
      isTrainingMode: !!isTrainingMode,
      autoAssign: autoAssignToggle,
      selectedTeamMembers,
      id: isTrainingMode
        ? undefined
        : selectedTeamMembers
            .map(m => m.adId)
            .filter((adId): adId is string => Boolean(adId))
            .join(','),
    });

    if (!isTrainingMode) {
      resetCount();
    }
  };

  const loadMoreTeamMembers =
    (filteredTeamMembers?.length ?? 0) > teamMembersPerPage;

  const currentMembers =
    teamMembersPerPage > (filteredTeamMembers?.length ?? 0)
      ? filteredTeamMembers?.length
      : teamMembersPerPage;

  const totalMembers = filteredTeamMembers?.length;

  const addTMCountText = t('Button.addXObjects', {
    count: currentCount,
    object:
      currentCount === 1 ? t('Generic.teamMember') : t('Generic.teamMembers'),
  });

  return (
    <Modal
      onClose={() => {
        setIsSelectAll(false);
        setSelectedTeamMembers([]);
        setHidePreviouslyAssigned(false);
        onClose();
      }}
      scrollMode="modal-body"
      show={isOpen}
      size="lg"
    >
      <ScrollModalHeader scrollRef={modalBodyRef}>
        <AssignedTeamMembersCount inNavbar={true} />
        {showReassignmentFeatures && showBanner && (
          <MessageBlock.Root severity="info" className="mt-6! text-start!">
            <div className="flex w-full items-start justify-between">
              <div>
                <Typography variant="body1" className="font-semibold!">
                  {t('TrainingPlans.teamMembers.recertificationBannerTitle')}
                </Typography>
                <Typography variant="body2" className="mt-1!">
                  {t('TrainingPlans.teamMembers.recertificationBannerBody')}
                </Typography>
              </div>
              <button
                onClick={() => {
                  setShowBanner(false);
                  localStorage.setItem('reassignment-banner-dismissed', 'true');
                }}
                className="ml-2 shrink-0 p-1"
                aria-label="Dismiss"
              >
                <IconX size={20} className="text-grayscale-gray5" />
              </button>
            </div>
          </MessageBlock.Root>
        )}
        {autoAssignPlansEpicFF && showAutoAssignToggle && !isTrainingMode && (
          <AutoAssignToggle
            isSelected={autoAssignToggle}
            onChange={setAutoAssignToggle}
            isCompliance={isCompliance}
          />
        )}
        <Searchbar
          elevation={1}
          onChange={onSearchInputChange}
          onClear={onClearSearchClick}
          placeholder={t('TrainingPlans.teamMembers.searchbarPlaceholder')}
          ref={searchInputRef}
          searchValue={searchTermDisplayed}
          className={cn('z-5! mt-6! mb-2!', {
            'mt-0!':
              autoAssignPlansEpicFF && showAutoAssignToggle && !isTrainingMode,
          })}
        />
      </ScrollModalHeader>
      <ModalBody ref={modalBodyRef}>
        <div>
          {showReassignmentFeatures && !isCompliance && (
            <div className="mb-4 flex items-start gap-4 sm:gap-2">
              <Checkbox
                checked={hidePreviouslyAssigned}
                onChange={() => setHidePreviouslyAssigned(prev => !prev)}
                label={t('TrainingPlans.teamMembers.hideFilter')}
                className="[&_label]:text-md! [&_label]:items-start! [&_label]:text-left! max-sm:[&_label]:text-sm!"
              />
              <Tooltip.Root
                delay={200}
                closeDelay={100}
                isOpen={isInfoTooltipOpen}
                onOpenChange={setIsInfoTooltipOpen}
              >
                <Focusable>
                  <IconInfoCircle
                    size={24}
                    className="text-grayscale-gray5 mt-[2px] shrink-0 cursor-pointer"
                    onClick={() => setIsInfoTooltipOpen(prev => !prev)}
                  />
                </Focusable>
                <Tooltip.Content
                  className="max-w-xs! px-6! py-4!"
                  placement="bottom start"
                >
                  <p className="text-base font-normal">
                    {t(
                      'TrainingPlans.teamMembers.tooltipIntro',
                      'A Team Member who',
                    )}
                  </p>
                  <div className="mt-2 flex items-start gap-2">
                    <CompletedXsm className="mt-0.5 size-5 shrink-0" />
                    <span className="text-base font-normal">
                      {t(
                        'TrainingPlans.teamMembers.tooltipCompletedDescription',
                      )}
                    </span>
                  </div>
                  <div className="mt-2 flex items-start gap-2">
                    <AlreadyAssignedXsm className="mt-0.5 size-5 shrink-0" />
                    <span className="text-base font-normal">
                      {t(
                        'TrainingPlans.teamMembers.tooltipAssignedDescription',
                      )}
                    </span>
                  </div>
                </Tooltip.Content>
              </Tooltip.Root>
            </div>
          )}

          {locations.length > 1 && (
            <LocationsFilter
              locations={locations}
              onClick={location => onChipClick(location)}
              onClose={location => onChipClick(location)}
              selectedLocations={filterBy}
            />
          )}
          <div className="mb-2 flex items-center justify-between">
            <span className="text-grayscale-gray7 font-bold">
              {isCompliance && !isTrainingMode
                ? t('Generic.unassignedTeamMembers')
                : t('TrainingPlans.teamMembers.count')}{' '}
              {`${currentMembers} ${t('Generic.of')} ${totalMembers}`}
            </span>
            <Button
              color="secondary"
              disabled={
                isTrainingMode ? false : selectableTeamMembers?.length === 0
              }
              onClick={onAssignAllTeamMembers}
              size="sm"
              variant="text"
              className="disabled:bg-transparent!"
            >
              <span className="text-grayscale-gray6 text-16 font-normal underline">
                {!isSelectAll ? t('Button.selectAll') : t('Button.clearAll')}
              </span>
            </Button>
          </div>

          {!!filteredTeamMembers?.length &&
            filteredTeamMembers
              .toSorted((a, b) =>
                a.name === b.name
                  ? 0
                  : (a.firstName ?? '').localeCompare(b.firstName ?? '') ||
                    (a.lastName ?? '').localeCompare(b.lastName ?? ''),
              )
              .slice(0, teamMembersPerPage)
              .map(member => {
                const memberAdId = member.adId;
                if (!memberAdId) {
                  return null;
                }
                return (
                  <TeamMemberCard
                    noHover
                    id={memberAdId}
                    isAssigned={
                      !!selectedTeamMembers.find(
                        selectedTeamMember =>
                          selectedTeamMember.adId === memberAdId,
                      )
                    }
                    isTrainingMode={!!isTrainingMode}
                    key={memberAdId}
                    name={`${member.prefName ?? member.firstName} ${member.lastName}`}
                    onAssignTeamMember={() => onAssignTeamMember(member)}
                    originallyAssigned={
                      isTrainingMode
                        ? (assignments
                            ?.filter(
                              teamMember =>
                                teamMember.status ===
                                Constants.TRAINING_PLANS.COMPLETED,
                            )
                            .map(it => it.userId)
                            .includes(member.adId) ?? false)
                        : !showReassignmentFeatures &&
                          (alreadyAssignedTeamMembers?.includes(member.adId) ??
                            false)
                    }
                    reassignEligible={reassignEligible}
                    simpleUserName={`${member.prefName ?? member.firstName} ${member.lastName}`}
                    planStatus={getTeamMemberPlanStatus(memberAdId)}
                    completionDate={getCompletionDate(memberAdId)}
                  />
                );
              })}
          {loadMoreTeamMembers && (
            <div className="mt-8 mb-4 flex justify-center">
              <Button color="secondary" onClick={onLoadMore}>
                {t('Button.loadMore')}
              </Button>
            </div>
          )}
          {}

          <Typography
            variant="body1"
            className="text-grayscale-gray6! my-4! text-center!"
          >
            {(isCompliance ? Boolean(filteredTeamMembers?.length) : true) &&
              t('Search.showingResultsFooter', {
                showingResults:
                  teamMembersPerPage > (filteredTeamMembers?.length ?? 0)
                    ? (filteredTeamMembers?.length ?? 0)
                    : teamMembersPerPage,
                numberOfResults:
                  (filteredTeamMembers?.length ?? 0) <= 100
                    ? (filteredTeamMembers?.length ?? 0)
                    : '100+',
                resultsText: pluralize(
                  filteredTeamMembers?.length ?? 0,
                  t('Generic.teamMember'),
                  t('Generic.teamMembers'),
                ),
              })}
          </Typography>
          {isCompliance && filteredTeamMembers?.length === 0 && (
            <Typography variant="body1" className="text-left">
              {t('Search.allTeamMembersAssigned')}
            </Typography>
          )}
        </div>
      </ModalBody>
      <ScrollModalFooter scrollRef={modalBodyRef}>
        <Button
          color="secondary"
          data-testid="ButtonNext"
          disabled={
            autoAssignPlansEpicFF && showAutoAssignToggle
              ? (!autoAssignToggleDifferentFromPlan && currentCount === 0) ||
                patchAutoAssign.isPending
              : currentCount === 0
          }
          onClick={onNextClick}
        >
          {autoAssignPlansEpicFF &&
          showAutoAssignToggle &&
          autoAssignToggleDifferentFromPlan &&
          currentCount === 0
            ? t('Button.continue')
            : addTMCountText}
        </Button>
      </ScrollModalFooter>
    </Modal>
  );
};

export default AddTeamMembersPopUp;
